import React, { useMemo } from 'react';
import { useAtom } from 'jotai/index';
import classNames from 'classnames';
import styles from '../css/index.module.scss';
import {
  widgetDataAtom, widgetDefaultsAtom, widgetGettingLoadingAtom,
  widgetInputsAtom,
  widgetParamsAtom,
  widgetSelectedLanguageAtom,
} from '../../../atoms/widgetAtoms';
import useWidget from '../../../hooks/UseWidget';
import WT from '../constants/widgetTranslations';
import EmptyHeight from '../../../components/EmptyHeight';
import IconButton from '../../../components/IconButton';
import WidgetMinusIcon from './WidgetMinusIcon';
import WidgetPlusIcon from './WidgetPlusIcon';
import WidgetDateInput from './WidgetDateInput';
import PartnersServices from '../../../services/PartnersServices';
import getFilteredWidgetSlots from '../../../helpers/getFilteredWidgetSlots';
import WidgetsServices from '../../../services/WidgetsServices';
import moment from 'moment/moment';
import useComponentVisible from '../../../hooks/UseComponentVisible';
// Замените на Icon.svg, когда будет файл: import Icon from '../../../assets/icons/Icon.svg';
import SlotIcon from '../../../assets/icons/clock.svg';

const MIDNIGHT_SEC = 24 * 3600;

function WidgetSlotsState() {
  const { t } = useWidget();
  const [widgetParams, setWidgetParams] = useAtom(widgetParamsAtom);
  const [widgetData] = useAtom(widgetDataAtom);
  const [widgetSelectedLanguage] = useAtom(widgetSelectedLanguageAtom);
  const [widgetInputs, setWidgetInputs] = useAtom(widgetInputsAtom);
  const [widgetGettingLoading] = useAtom(widgetGettingLoadingAtom);
  const [widgetDefaults] = useAtom(widgetDefaultsAtom);
  const { ref: slotDropdownRef, isComponentVisible: isSlotDropdownVisible, setIsComponentVisible: setSlotDropdownVisible } = useComponentVisible(false);

  const filteredSlots = useMemo(() => getFilteredWidgetSlots(widgetData.slots, widgetParams), [widgetData.slots]);
  const useTelegramSlotView = widgetData?.send_through_telegram_bot === true;

  const slotsBeforeMidnight = useMemo(() => (filteredSlots || []).filter((s) => s < MIDNIGHT_SEC), [filteredSlots]);
  const slotsAfterMidnight = useMemo(() => (filteredSlots || []).filter((s) => s >= MIDNIGHT_SEC), [filteredSlots]);
  const showAfterMidnightLabel = slotsAfterMidnight.length > 0;
  const nextDayLabel = showAfterMidnightLabel
    ? `${moment(widgetParams.date).add(1, 'day').locale(widgetSelectedLanguage?.code || 'ru').format('D MMMM')} ${t(WT.afterMidnight)}`
    : null;

  const slotButtonText = widgetInputs.slot
    ? PartnersServices.convertSecondsToFormat(widgetInputs.slot)
    : t(WT.chooseTime);

  const onSlotSelect = (slot) => {
    setWidgetInputs({ slot });
    setSlotDropdownVisible(false);
  };

  return (
    <>
      <div className={styles.slotsState}>
        <div className={styles.row}>
          <p className={styles.text}>
            {widgetParams.persons}
            {' '}
            {t(WT.person, widgetParams.persons)}
          </p>
          <div className={styles.buttons}>
            <IconButton
              onClick={() => setWidgetParams({ persons: Math.max(widgetParams.persons - 1, 1) })}
              svgPath={WidgetMinusIcon()}
              wrapperClassName={styles.button}
              noIconFilter
            />
            <IconButton
              onClick={() => setWidgetParams({ persons: Math.max(widgetParams.persons + 1, 1) })}
              svgPath={WidgetPlusIcon()}
              wrapperClassName={styles.button}
              noIconFilter
            />
          </div>
        </div>
        <EmptyHeight height={24} />
        <div className={styles.row}>
          <WidgetDateInput
            value={widgetParams.date}
            onChange={(val) => setWidgetParams({ date: val })}
            locale={widgetSelectedLanguage.code}
          />
        </div>
        <EmptyHeight height={24} />

        <div className={styles.mainPart}>
          {widgetGettingLoading
            ? <p className={styles.text}>{t(WT.loading)}</p>
            : !filteredSlots?.length
              ? (
                <p className={styles.text}>
                  {widgetData.message || t(WT.noSlotsForDate)}
                  {WidgetsServices.shouldShowManagerPhoneComponent({
                    widgetData, widgetDefaults, widgetParams, styles, message: widgetData.message || t(WT.noSlotsForDate),
                  })}
                </p>
              )
              : useTelegramSlotView
                ? (
                  <div className={styles.slotDropdown} ref={slotDropdownRef}>
                    <div
                      className={styles.slotDropdownButton}
                      onClick={() => setSlotDropdownVisible(!isSlotDropdownVisible)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSlotDropdownVisible(!isSlotDropdownVisible); } }}
                    >
                      <p className={styles.text}>{slotButtonText}</p>
                      <img className={styles.slotDropdownButtonImg} src={SlotIcon} alt="" />
                    </div>
                    {isSlotDropdownVisible && (
                      <div className={styles.slotDropdownList}>
                        {slotsBeforeMidnight.map((slot) => (
                          <div
                            key={slot}
                            className={styles.slotDropdownItem}
                            onClick={() => onSlotSelect(slot)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSlotSelect(slot); } }}
                          >
                            {PartnersServices.convertSecondsToFormat(slot)}
                          </div>
                        ))}
                        {showAfterMidnightLabel && nextDayLabel && (
                          <div className={styles.slotDropdownItemLabel}>
                            {nextDayLabel}
                          </div>
                        )}
                        {slotsAfterMidnight.map((slot) => (
                          <div
                            key={slot}
                            className={styles.slotDropdownItem}
                            onClick={() => onSlotSelect(slot)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSlotSelect(slot); } }}
                          >
                            {PartnersServices.convertSecondsToFormat(slot)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )
                : (
                  <div className={styles.slotsRow}>
                    {filteredSlots.map((slot) => (
                      <div
                        key={slot}
                        onClick={() => setWidgetInputs({ slot })}
                        className={classNames(styles.slot, styles.clickable, { [styles.slotSelected]: widgetInputs.slot === slot })}
                      >
                        <p className={styles.text}>{PartnersServices.convertSecondsToFormat(slot)}</p>
                      </div>
                    ))}
                  </div>
                )}
        </div>
      </div>
    </>
  );
}

export default WidgetSlotsState;

{ /* typeof widgetData.max_guests === 'number' && widgetData.max_guests < widgetParams.persons ? (
            <p className={styles.mediumText}>
              Ваше бронирование требует большого внимания! Пожалуйста, позвоните в ресторан по номеру:
              {' '}
              <a
                href={`tel:${'+7 999 123 34 55'}`}
                className={classNames(styles.phoneNumber, styles.clickableOpacity)}
              >
                +7 999 123 34 55
              </a>
            </p>
          ) : */ }
